import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  User,
  Users,
  Building2,
  AlertCircle,
  Eye,
  Download,
  DollarSign
} from 'lucide-react'
import { useVendorDashboard } from '../VendorDashboard'

const VendorPurchaseOrders = () => {
  const { isLoading } = useVendorDashboard()

  // Mock data - replace with actual API data
  const [purchaseOrders] = useState([
    {
      id: 'PO-2024-001',
      companyName: 'Tech Corp Inc.',
      resourceRequired: 'Senior React Developer',
      status: 'pending',
      receivedDate: '2024-01-15',
      dueDate: '2024-01-25',
      resourceCount: 2,
      budget: 150000,
      requirements: '5+ years React, TypeScript, Redux',
      ajaManager: 'John Smith',
      description: 'Need 2 senior React developers for a 6-month project'
    },
    {
      id: 'PO-2024-002',
      companyName: 'Digital Solutions Ltd.',
      resourceRequired: 'Full Stack Developer',
      status: 'accepted',
      receivedDate: '2024-01-10',
      dueDate: '2024-01-20',
      resourceCount: 1,
      budget: 120000,
      requirements: 'Node.js, React, PostgreSQL',
      ajaManager: 'Sarah Johnson',
      description: 'Full stack developer for API development'
    },
    {
      id: 'PO-2024-003',
      companyName: 'Innovation Labs',
      resourceRequired: 'DevOps Engineer',
      status: 'pending',
      receivedDate: '2024-01-18',
      dueDate: '2024-01-28',
      resourceCount: 1,
      budget: 140000,
      requirements: 'AWS, Docker, Kubernetes, CI/CD',
      ajaManager: 'Mike Davis',
      description: 'DevOps engineer for infrastructure setup'
    }
  ])

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: 'bg-warning-100 dark:bg-warning-900/30',
        text: 'text-warning-700 dark:text-warning-300',
        icon: <Clock className="w-4 h-4" />,
        label: 'Pending'
      },
      accepted: {
        bg: 'bg-success-100 dark:bg-success-900/30',
        text: 'text-success-700 dark:text-success-300',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Accepted'
      },
      rejected: {
        bg: 'bg-error-100 dark:bg-error-900/30',
        text: 'text-error-700 dark:text-error-300',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Rejected'
      }
    }

    const config = statusConfig[status] || statusConfig.pending
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading purchase orders...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
            Purchase Orders
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Manage purchase orders received from AJA managers
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="card p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-1">
              How Purchase Orders Work
            </h3>
            <p className="text-xs text-primary-700 dark:text-primary-300">
              When a company needs resources, they place a PO with AJA. AJA managers then forward these POs to you. 
              Review the requirements, accept if you have suitable resources, and proceed with interviews.
            </p>
          </div>
        </div>
      </div>

      {/* Purchase Orders List */}
      <div className="space-y-4">
        {purchaseOrders.length === 0 ? (
          <div className="card p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
              No Purchase Orders Yet
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              Purchase orders from AJA managers will appear here once companies request your resources.
            </p>
          </div>
        ) : (
          purchaseOrders.map((po, index) => (
            <motion.div
              key={po.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                          {po.id}
                        </h3>
                        {getStatusBadge(po.status)}
                      </div>
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        Received from AJA Manager: <span className="font-semibold">{po.ajaManager}</span>
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Company</p>
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {po.companyName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Resource Required</p>
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {po.resourceRequired}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Resources Needed</p>
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {po.resourceCount} {po.resourceCount === 1 ? 'Resource' : 'Resources'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Budget</p>
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          ₹{po.budget.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Requirements</p>
                    <p className="text-sm text-secondary-700 dark:text-secondary-300">{po.requirements}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Description</p>
                    <p className="text-sm text-secondary-700 dark:text-secondary-300">{po.description}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-secondary-600 dark:text-secondary-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Received: {new Date(po.receivedDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>Due: {new Date(po.dueDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 lg:min-w-[200px]">
                  {po.status === 'pending' && (
                    <>
                      <button className="btn-primary text-sm font-semibold py-2">
                        Accept PO
                      </button>
                      <button className="btn-outline text-sm font-semibold py-2">
                        Reject PO
                      </button>
                    </>
                  )}
                  <button className="btn-outline text-sm font-semibold py-2 flex items-center justify-center gap-2">
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button className="btn-outline text-sm font-semibold py-2 flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" />
                    Download PO
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  )
}

export default VendorPurchaseOrders

